import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, input, Injector, model, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { TranslateService, TranslatePipe } from '@ngx-translate/core';


import { DynamicFormField, FieldSpecialControlData, FieldSpecialControlDataValue } from '../../../models/dynamic-form';
import { FormSpecialLogic } from '../../../utils/form-special-logic/form-special-logic';

import { DynamicFieldLabel } from '../dynamic-field-label/dynamic-field-label';
import { DynamicFieldDatepicker } from '../dynamic-field-datepicker/dynamic-field-datepicker';
import { DynamicFieldInput } from '../dynamic-field-input/dynamic-field-input';
import { DynamicFieldSelect } from '../dynamic-field-select/dynamic-field-select';
import { DynamicFieldRadioButtons } from '../dynamic-field-radio-buttons/dynamic-field-radio-buttons';
import { DynamicFieldCheckbox } from '../dynamic-field-checkbox/dynamic-field-checkbox';
import { DynamicFieldTextarea } from '../dynamic-field-textarea/dynamic-field-textarea';
import { DynamicHost } from '../../../directives/dynamic-host';
import { FormService } from '../../../services/form-service';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';

import
{
  MatDialog,
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
} from '@angular/material/dialog';



const componentMap: Record<string, any> = {
  ['label']: DynamicFieldLabel,
  ['date-picker']: DynamicFieldDatepicker,
  ['input']: DynamicFieldInput,
  ['select']: DynamicFieldSelect,
  ['radio-button']: DynamicFieldRadioButtons,
  ['checkbox']: DynamicFieldCheckbox,
  ['textarea']: DynamicFieldTextarea,
};

@Component( {
  selector: 'app-dynamic-field-special-control',
  imports: [DatePipe, TranslatePipe, DynamicHost, ReactiveFormsModule, MatIconModule, MatButtonModule, MatCardModule, MatDialogModule],
  templateUrl: './dynamic-field-special-control.html',
  styleUrl: './dynamic-field-special-control.css',
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class DynamicFieldSpecialControl implements AfterViewInit, OnInit
{
  constructor(
    private _translateService: TranslateService,
    private _formService: FormService,
    private _formEspecialLogic: FormSpecialLogic,
    private injector: Injector,
    private _cdr: ChangeDetectorRef )
  {
    this._translateService.onLangChange.subscribe( async ( lang ) =>
    {
      this.currentLanguage = lang.lang;
      await this.loadDynamicFieldComponent();
    } );
  }

  public field = input.required<DynamicFormField>();
  public control = model.required<AbstractControl>();
  public formControl!: FormControl;
  public specialControlFields: DynamicFormField[] = [];
  public formGroup: FormGroup | undefined;
  public specialControlData: FieldSpecialControlData | undefined;
  public entries: any[] = [];
  public editing: boolean = false;
  public hasSpecialLogic: boolean = false;
  public dialog = inject( MatDialog );
  public currentLanguage = 'en';

  @ViewChildren( DynamicHost ) dynamicHosts!: QueryList<DynamicHost>;

  // subscriptions
  private _formValueChangesSub?: Subscription;
  private _dialogAfterClosedSub?: Subscription;

  async ngOnInit(): Promise<void>
  {
    this.currentLanguage = this._translateService.getCurrentLang() || 'en';
    this.formControl = this.control() as FormControl;
    this.specialControlFields = [...this.field().specialControlFields];
    this.hasSpecialLogic = await this._formEspecialLogic.HasSpecialLogic( `${this.field().fieldType}` );

    this.specialControlData = this.formControl.value ?
      JSON.parse( this.formControl.value ) as FieldSpecialControlData
      : { values: [], removedIds: [], hasChanges: false, updatedIds: [], history: [] } as FieldSpecialControlData;
    this.entries = this.specialControlData?.values || [];
    this.entries.forEach( entry => entry.mode = 'view' );
  }

  async ngAfterViewInit(): Promise<void>
  {
    await this.loadDynamicFieldComponent();
    this._cdr.detectChanges();
  }

  ngOnDestroy(): void
  {
    // unsubscribe any active subscriptions
    this._unsubscribeFormValueChanges();
    this._unsubscribeDialogAfterClosed();
  }

  private _unsubscribeFormValueChanges(): void
  {
    if ( this._formValueChangesSub )
    {
      this._formValueChangesSub.unsubscribe();
      this._formValueChangesSub = undefined;
    }
  }

  private _unsubscribeDialogAfterClosed(): void
  {
    if ( this._dialogAfterClosedSub )
    {
      this._dialogAfterClosedSub.unsubscribe();
      this._dialogAfterClosedSub = undefined;
    }
  }

  async add()
  {
    const newRow: FieldSpecialControlDataValue = {
      id: `${this.entries.length + 1}`,
      fields: this.specialControlFields.map( ( field ) =>
      {
        return { ...field };
      } ),
      mode: 'add',
    };
    this.entries.push( newRow );
    this.editing = true;
    // unsubscribe previous form value changes before creating a new formGroup
    this._unsubscribeFormValueChanges();

    this.formGroup = await this._formService.GetFormGroup( newRow.fields, 'edit' );
    this.loadDynamicFieldComponent();
    this._cdr.detectChanges();
  }

  async edit( row: FieldSpecialControlDataValue )
  {
    this.editing = true;
    row.mode = 'edit';

    // unsubscribe previous form value changes before creating a new formGroup
    this._unsubscribeFormValueChanges();

    this.formGroup = await this._formService.GetFormGroup( row.fields, 'edit' );
    this.loadDynamicFieldComponent();
    this._cdr.detectChanges();
  }

  async cancel( row: FieldSpecialControlDataValue )
  {

    if ( row.mode === 'add' )
    {
      this.delete( row );
    }

    this.formGroup?.reset();
    this.formGroup = undefined;

    // unsubscribe previous form value changes before creating a new formGroup
    this._unsubscribeFormValueChanges();

    row.mode = 'view';
    this.editing = false;
    this._cdr.detectChanges();
  }

  async delete( row: FieldSpecialControlDataValue )
  {
    var entryIndex = this.entries.indexOf( row );
    if ( entryIndex > -1 )
    {
      this.entries.splice( entryIndex, 1 );
      this.specialControlData!.removedIds.push( row.id );
      this.specialControlData!.hasChanges = true;
    }

    this._cdr.detectChanges();
  }

  async save( row: FieldSpecialControlDataValue )
  {

    // trigger save logic here
    this.formGroup?.markAllAsTouched();
    if ( this.formGroup?.valid )
    {
      // update special control data values
      await this.updateSpecialControlData( row );

      // mark that there are changes
      this.specialControlData!.hasChanges = true;
      this.specialControlData!.values = this.entries;
      this.formControl.setValue( JSON.stringify( this.specialControlData ) );

      // reset form group and set all rows to view mode
      this.formGroup = undefined;

      // unsubscribe previous form value changes before creating a new formGroup
      this._unsubscribeFormValueChanges();

      row.mode = 'view';
      this.editing = false;

      this._cdr.detectChanges();
    }
  }

  async loadDynamicFieldComponent(): Promise<void>
  {
    this._cdr.detectChanges();

    if ( !this.dynamicHosts || this.dynamicHosts.length === 0 ) return;

    this.dynamicHosts.forEach( host =>
    {

      host.viewContainerRef.clear();

      const fieldType = host.field().renderType.element;
      const componentType = componentMap[fieldType];
      if ( !componentType ) return;
      const componentRef: ComponentRef<any> = host.viewContainerRef.createComponent( componentType, {
        injector: this.injector
      } );

      if ( componentRef.instance.hasOwnProperty( 'type' ) )
      {
        componentRef.instance.type = this.field().renderType.type;
      }
      if ( componentRef.instance.hasOwnProperty( 'label' ) )
      {
        componentRef.instance.label = this.getFieldLabel( host.field() );
      }
      if ( componentRef.instance.hasOwnProperty( 'classes' ) )
      {
        componentRef.instance.classes = this.field().renderType.classes;
      }
      if ( componentRef.instance.hasOwnProperty( 'field' ) )
      {
        componentRef.instance.field = host.field;
      }
      if ( componentRef.instance.hasOwnProperty( 'control' ) )
      {
        componentRef.instance.control = host.control;
      }

      componentRef.changeDetectorRef.detectChanges();
    } );

    // load special logic if any
    await this.loadSpecialLogic();

    this._cdr.detectChanges();
  }

  getFormControl( field: DynamicFormField ): AbstractControl
  {
    const controlKey = `${field.name || field.fieldType}-${field.fieldId.toString()}`;
    return this.formGroup!.get( controlKey ) as AbstractControl;
  }

  async confirmAction( action: 'delete' | 'cancel' | 'save', row: FieldSpecialControlDataValue ): Promise<void>
  {
    this._cdr.detectChanges();

    // update special control data before confirmation
    if ( this.formGroup )
    {
      await this.updateSpecialControlData( row );
    }

    const dialogRef = this.dialog.open( ConfimationDialog, {
      data: {
        action: action,
        elements: row.fields
      },
    } );

    // unsubscribe previous dialog subscription to avoid leaks
    this._unsubscribeDialogAfterClosed();
    this._dialogAfterClosedSub = dialogRef.afterClosed().subscribe( result =>
    {
      if ( !!result )
      {
        if ( action === 'delete' ) this.delete( row );
        if ( action === 'cancel' ) this.cancel( row );
        if ( action === 'save' ) this.save( row );
      }
    } );
  }

  async updateSpecialControlData( row: FieldSpecialControlDataValue ): Promise<void>
  {

    row.fields.forEach( field =>
    {
      const control = this.getFormControl( field );
      row.fields[row.fields.findIndex( f => f.fieldId === field.fieldId )].value = control.value;
    } );
  }

  async loadSpecialLogic(): Promise<void>
  {
    // subscribe to form changes
    if ( this.hasSpecialLogic )
    {
      // initial execution
      await this._formEspecialLogic.Execute( `${this.field().fieldType}`, this.formGroup!.controls, this.entries );

      if ( !this.formGroup ) return;

      // unsubscribe previous subscription if any, then subscribe to changes
      this._unsubscribeFormValueChanges();
      this._formValueChangesSub = this.formGroup.valueChanges.subscribe( async () =>
      {
        await this._formEspecialLogic.Execute( `${this.field().fieldType}`, this.formGroup!.controls );
      } );
    }
  }

  getFieldLabel( field: DynamicFormField ): string
  {
    const labelObj = field.label!;
    const labelText = labelObj[this.currentLanguage as keyof typeof labelObj] || labelObj['en'] || '';
    return labelText;
  }

}

@Component( {
  selector: 'dialog-confirmation',
  templateUrl: '../../../utils/dialog-confirmation-component/dialog-confirmation-component.html',
  imports: [DatePipe, MatDialogTitle, MatDialogContent, MatDialogModule, MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class ConfimationDialog
{
  data = inject( MAT_DIALOG_DATA );
}
